import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { captureEvent } from '../../lib/analytics';
import { useApiClient } from '../../services/apiClient';
import { buildSessionData } from '../../utils/buildSessionData';
import { assessBikeFit } from '../../utils/fitAssessment';
import { saveAnalysisState } from '../../utils/sessionPersistence';
import type { PedalAnalysis, PelvicRockResult, SimpleFrame } from '../../modules/ProcessingPipeline';
import type { BikeType, Flexibility, PainPoint, RidingExperience, RidingStyle } from '../../constants/bikeConfig';
import FitActions from './FitActions';
import FitEvidence from './FitEvidence';
import FitGate from './FitGate';
import FitMetrics from './FitMetrics';
import FitNextSteps from './FitNextSteps';
import FitVerdict from './FitVerdict';
import FitWorkingWell from './FitWorkingWell';
import type { FitReport } from './types';
import './fitResults.css';

interface Props {
  sessionId: string;
  sideFrames: SimpleFrame[];
  frontFrames: SimpleFrame[];
  pedalAnalysis?: {
    bdc: PedalAnalysis | null;
    tdc: PedalAnalysis | null;
    trunkAngleAvg: number | null;
    trunkAngleDivergence: boolean;
    pelvicRock?: PelvicRockResult | null;
  };
  bikeType: BikeType;
  ridingStyle: RidingStyle;
  experience: RidingExperience;
  flexibility: Flexibility;
  painPoints: PainPoint[];
  isSignedIn: boolean;
  annotatedImageUrls?: Record<string, string>;
  onStartOver: () => void;
  onCreditConsumed?: () => void;
}

type LoadState = 'loading' | 'ready' | 'error';
type ReportState = 'idle' | 'generating' | 'done' | 'error';

const FitResultsPage: React.FC<Props> = ({
  sessionId, sideFrames, frontFrames, pedalAnalysis,
  bikeType, ridingStyle, experience, flexibility, painPoints,
  isSignedIn, annotatedImageUrls, onStartOver, onCreditConsumed,
}) => {
  const api = useApiClient();
  const navigate = useNavigate();

  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [report, setReport] = useState<FitReport | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'bdc' | 'tdc'>('bdc');

  const [reportState, setReportState] = useState<ReportState>('idle');
  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);

  /**
   * Generating the PDF consumes the credit, which would otherwise flip
   * entitlement to false and re-lock the page the rider just paid for. Held
   * locally so that cannot happen mid-session; a reload still re-locks, which
   * is the backend issue the billing project resolves.
   */
  const [unlockedThisSession, setUnlockedThisSession] = useState(false);

  const bdc = pedalAnalysis?.bdc ?? null;
  const tdc = pedalAnalysis?.tdc ?? null;
  const hasAnalysis = bdc !== null && tdc !== null;

  const sessionData = useMemo(() => {
    if (!hasAnalysis || !pedalAnalysis) return null;
    const assessment = assessBikeFit(
      bikeType, ridingStyle, bdc!.angles, tdc!.angles,
      pedalAnalysis.trunkAngleAvg, experience, flexibility,
    );
    return buildSessionData(
      sessionId, bikeType, ridingStyle, experience, flexibility, painPoints,
      bdc!, tdc!, pedalAnalysis.trunkAngleAvg, pedalAnalysis.trunkAngleDivergence,
      assessment, pedalAnalysis.pelvicRock ?? null,
    );
  }, [hasAnalysis, pedalAnalysis, sessionId, bikeType, ridingStyle, experience,
      flexibility, painPoints, bdc, tdc]);

  const load = useCallback(async () => {
    if (!sessionData) return;
    setLoadState('loading');
    setLoadError(null);
    try {
      const result = await api.getFitReport(sessionData as unknown as Record<string, unknown>);
      setReport(result);
      setLoadState('ready');
      captureEvent('report_viewed', { entitled: result.entitled });
    } catch (err) {
      // Never fall through to an empty page — the old dashboard's silent
      // blank "Your Fit" is exactly what this replaces.
      setLoadError(err instanceof Error ? err.message : 'Could not load your fit');
      setLoadState('error');
    }
    // `api` is deliberately excluded: useApiClient() returns a fresh object
    // literal on every render (see CLAUDE.md), so including it here means
    // `load`'s identity changes every render, the effect below refires every
    // render, and the resulting setState triggers another render — an
    // unbounded refetch loop with no visible symptom beyond the page never
    // settling. `sessionData` is stably memoized and is the only real
    // dependency; nothing here should ever legitimately reference `api`
    // through this list.
  }, [sessionData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleBeforeNavigate = useCallback(async () => {
    if (pedalAnalysis) {
      await saveAnalysisState(
        sessionId, sideFrames, frontFrames, pedalAnalysis,
        bikeType, ridingStyle, experience, flexibility, painPoints,
      );
    }
  }, [sessionId, sideFrames, frontFrames, pedalAnalysis, bikeType, ridingStyle,
      experience, flexibility, painPoints]);

  const handleUnlock = useCallback(async () => {
    captureEvent('upgrade_prompt_clicked');
    await handleBeforeNavigate();
    sessionStorage.setItem('bikendure_return_url', '/video-upload');
    navigate(isSignedIn ? '/billing' : '/sign-in');
  }, [handleBeforeNavigate, isSignedIn, navigate]);

  const handleGenerateReport = useCallback(async () => {
    if (!sessionData || !bdc || !tdc) return;
    captureEvent('report_generation_started');
    setReportState('generating');
    setReportError(null);

    const extractFrame = (frameIndex: number): string | undefined => {
      const frame = sideFrames[frameIndex];
      if (!frame?.imageData) return undefined;
      const canvas = document.createElement('canvas');
      canvas.width = frame.imageData.width;
      canvas.height = frame.imageData.height;
      canvas.getContext('2d')!.putImageData(frame.imageData, 0, 0);
      return canvas.toDataURL('image/jpeg', 0.85);
    };

    try {
      const frameImages: { bdc?: string; tdc?: string } = {};
      const bdcImg = extractFrame(bdc.representativeFrameIndex);
      const tdcImg = extractFrame(tdc.representativeFrameIndex);
      if (bdcImg) frameImages.bdc = bdcImg;
      if (tdcImg) frameImages.tdc = tdcImg;

      const result = await api.generateReport({
        session_data: sessionData as unknown as Record<string, unknown>,
        annotated_image_urls: annotatedImageUrls,
        frame_images: Object.keys(frameImages).length > 0 ? frameImages : undefined,
      });

      const backendUrl = process.env.REACT_APP_BACKEND_URL ?? 'http://localhost:5000';
      setReportUrl(`${backendUrl}${result.report_url}`);
      setReportState('done');
      setUnlockedThisSession(true);
      captureEvent('report_generated');
      onCreditConsumed?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to generate report';
      captureEvent('report_generation_error', { error: msg });
      setReportError(msg);
      setReportState('error');
    }
    // `api` excluded for the same reason as in `load` above — its identity
    // changes every render, so including it would give this callback a new
    // identity on every render too. Harmless here since it only runs from a
    // click handler rather than an effect, but the pattern doesn't belong in
    // this file at all.
  }, [sessionData, bdc, tdc, sideFrames, annotatedImageUrls, onCreditConsumed]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!hasAnalysis) {
    return (
      <div className="fr-stage">
        <div className="fr-container">
          <div className="fr-wrap">
            <p className="fr-state">
              We couldn't measure this video. The most common causes are a camera angle that
              isn't square-on, loose clothing hiding the joints, or part of the bike out of
              frame.
            </p>
            <div className="fr-foot">
              <button type="button" className="fr-btn fr-btn--pri" onClick={onStartOver}>
                Film it again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadState === 'loading') {
    return (
      <div className="fr-stage">
        <div className="fr-container">
          <div className="fr-wrap"><p className="fr-state">Reading your fit…</p></div>
        </div>
      </div>
    );
  }

  if (loadState === 'error' || !report) {
    return (
      <div className="fr-stage">
        <div className="fr-container">
          <div className="fr-wrap">
            <p className="fr-state fr-state--error">{loadError}</p>
            <div className="fr-foot">
              <button type="button" className="fr-btn fr-btn--pri" onClick={load}>Try again</button>
              <button type="button" className="fr-btn fr-btn--gho" onClick={onStartOver}>
                Start a new analysis
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const entitled = report.entitled || unlockedThisSession;
  const changeCount = report.actions.filter(a => a.kind === 'change').length;
  const firstChange = report.actions.find(a => a.kind === 'change')?.title ?? null;

  const handleSelectMetric = (id: string | null) => {
    setSelectedMetric(id);
    if (id) {
      const metric = report.metrics.find(m => m.id === id);
      if (metric) setActiveView(metric.evidence_view);
    }
  };

  return (
    <div className="fr-stage">
      <div className="fr-container">
        <div className="fr-wrap">
          <FitVerdict verdict={report.verdict} context={report.context} />

          <div className="fr-cols" style={{ marginTop: 40 }}>
            <div className="fr-sticky">
              <div className="fr-shead"><h2>Your pedal stroke</h2></div>
              <p className="fr-sub">
                Two frames from your video — the top and the bottom of the stroke. Tap any
                measurement below and we'll show you where it lives on the bike.
              </p>
              <FitEvidence
                evidence={report.evidence}
                frames={sideFrames}
                bdc={bdc}
                tdc={tdc}
                activeView={activeView}
                highlightId={selectedMetric}
                onViewChange={setActiveView}
              />
            </div>

            <div>
              <div className="fr-shead">
                <h2>Do this first</h2>
                <span>{changeCount === 1 ? '1 change' : `${changeCount} changes`}</span>
              </div>
              <p className="fr-sub">
                One change at a time. Ride each one before touching anything else — you can't
                tell what worked if you move two things at once.
              </p>
              <FitActions actions={report.actions} entitled={entitled} onUnlock={handleUnlock} />
            </div>
          </div>

          <section className="fr-section">
            <div className="fr-shead">
              <h2>Your numbers</h2>
              <span>{report.metrics.length} measurements</span>
            </div>
            <p className="fr-sub">
              Every measurement, free — values, ideal ranges, and which way you're off. Tap a
              row to see where it lives on the bike.
            </p>
            <FitMetrics
              metrics={report.metrics}
              selectedId={selectedMetric}
              onSelect={handleSelectMetric}
              onUnlock={handleUnlock}
            />
          </section>

          {report.working_well.length > 0 && (
            <section className="fr-section">
              <div className="fr-shead"><h2>What's already right</h2></div>
              <FitWorkingWell items={report.working_well} />
            </section>
          )}

          <section className="fr-section">
            <div className="fr-shead"><h2>What happens next</h2></div>
            <FitNextSteps firstChange={firstChange} onNewAnalysis={onStartOver} />
          </section>

          <div className="fr-foot">
            {entitled && reportState !== 'done' && (
              <button
                type="button"
                className="fr-btn fr-btn--sec"
                onClick={handleGenerateReport}
                disabled={reportState === 'generating'}
              >
                {reportState === 'generating' ? 'Preparing your PDF…' : 'Generate PDF report'}
              </button>
            )}
            {reportState === 'done' && reportUrl && (
              <a className="fr-btn fr-btn--sec" href={reportUrl}
                 target="_blank" rel="noopener noreferrer">
                Download PDF report
              </a>
            )}
            <button type="button" className="fr-btn fr-btn--gho" onClick={onStartOver}>
              Start a new analysis
            </button>
          </div>

          {reportState === 'error' && reportError && (
            <div className="fr-inline-error">
              Couldn't generate the PDF: {reportError}{' '}
              <button type="button" className="fr-lock-link" onClick={handleGenerateReport}>
                Try again
              </button>
            </div>
          )}

          <p className="fr-fine">
            Bikendure Fit measures joint angles from a single side-on video. Home lighting,
            camera angle and loose clothing all move the numbers by a degree or two, so treat
            these as a well-informed starting point rather than a clinical measurement. Nothing
            here is medical advice — if you're riding through pain, see a physio.
          </p>
        </div>
      </div>

      {!entitled && <FitGate variant="bar" onUnlock={handleUnlock} />}
    </div>
  );
};

export default FitResultsPage;
